import { useEffect, useState } from 'react'
import { get, post } from '../../../infrastructure/api'
import { createContainer } from 'unstated-next'
import { changeI18NextLanguage, Language, storageLanguageKey } from '../../../infrastructure/i18nextHelper'
import { UserSettings } from '../../../infrastructure/signIn/models'
import { UserContextContainer } from '../../signIn/userContext'
import { hasFeature } from '../../../infrastructure/feature'

export type UserSettingsChoice = { label: string, value: string, format: string, shortFormat?: string, shortYearFormat?: string }

export const availableDateFormat: UserSettingsChoice[] = [
    { label: 'DD/MM/YYYY', value: 'Standard', format: 'DD/MM/YYYY', shortFormat: 'DD/MM', shortYearFormat: 'DD/MM/YY' },
    { label: 'MM/DD/YYYY', value: 'US', format: 'MM/DD/YYYY', shortFormat: 'MM/DD', shortYearFormat: 'MM/DD/YY' }]

export const availableDecimalSeparators: UserSettingsChoice[] = [
    { label: '123456.78', value: 'Dot', format: 'en-US' },
    { label: '123456,78', value: 'Comma', format: 'fr-FR' }]

export const availableLanguages: Language[] = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'pt', label: 'Português' }
]

const defaultDateFormat: UserSettingsChoice = availableDateFormat.find(x => x.value === 'US')!
const defaultDecimalSeparator: UserSettingsChoice = availableDecimalSeparators.find(x => x.value === 'Dot')!
const defaultUserSettings: UserSettings = { language: 'en', dateFormat: defaultDateFormat.value, decimalSeparator: defaultDecimalSeparator.value }

function useUserSettingsContext() {

    let userContext = UserContextContainer.useContainer()
    
    useEffect(() => {
        if (userContext.isLoggedIn) fetchOrInitializeUserSettings()
    }, []);
    
    let [userSettings, setUserSettings] = useState<UserSettings>(defaultUserSettings)

    async function changeUserSettings(newSettings: UserSettings) {
        if (newSettings.language !== userSettings.language)
            changeLanguage(newSettings.language)

        setUserSettings(newSettings)
        await post('userSettings', newSettings)
    }

    async function fetchOrInitializeUserSettings() {
        let settings = await get<UserSettings | null>('userSettings')
        if (!settings){
            let persistedLanguage = localStorage.getItem(storageLanguageKey)
            settings = { ...defaultUserSettings, language: persistedLanguage ? persistedLanguage : defaultUserSettings.language }
            post('userSettings', settings)
        }
        setUserSettings(settings)
    }

    function changeLanguage(language: string) {
        changeI18NextLanguage(availableLanguages.find(x => x.code === language)?.code ?? 'en')
    }

    function propertyNullIfEmpty(prop: keyof UserSettings): string | null {
        return userSettings[prop] !== '' ? userSettings[prop] : null
    }

    function getLanguage(): string { return propertyNullIfEmpty('language') ?? 'en' }

    function getDateFormat(fallback: string | null = null): string {
        if (!hasFeature('DateFormatFromAppsettings')) return fallback ?? defaultDateFormat.format
        return availableDateFormat.find(x => x.value === userSettings.dateFormat)?.format ?? defaultDateFormat.format
    }

    function getShortYearDateFormat(fallback: string | null = null): string {
        if (!hasFeature('DateFormatFromAppsettings')) return fallback ?? defaultDateFormat.shortYearFormat!
        return availableDateFormat.find(x => x.value === userSettings.dateFormat)?.shortYearFormat ?? defaultDateFormat.shortYearFormat!
    }

    function getShortDateFormat(fallback: string | null = null): string {
        if (!hasFeature('DateFormatFromAppsettings')) return fallback ?? defaultDateFormat.shortFormat!
        return availableDateFormat.find(x => x.value === userSettings.dateFormat)?.shortFormat ?? defaultDateFormat.shortFormat!
    }

    function getFormatElapsedTime(fallback: string | null | undefined): string {
        if (!fallback)
            return '00h00m00s'

        const match = fallback.match(/^(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})/)

        if (!match)
            return fallback

        const days = Number(match[1] ?? 0)
        const hours = Number(match[2]) + (days * 24)
        const minutes = match[3]
        const seconds = match[4]

        return `${hours.toString().padStart(2, '0')}h${minutes}m${seconds}s`
    }

    return { changeUserSettings, getDateFormat, getShortDateFormat, getLanguage, fetchOrInitializeUserSettings, ChangeLanguage: changeLanguage, userSettings, getShortYearDateFormat, getFormatElapsedTime }
}

export let UserSettingsContextContainer = createContainer(useUserSettingsContext)